(() => {
  const POPUP_SELECTOR = '[data-custom-popup]';
  const CLOSE_SELECTOR = '[data-custom-popup-close]';

  const isThemeEditor = () => {
    return window.Shopify && window.Shopify.designMode;
  };

  const getStorageKey = (popup) => {
    const popupId = popup.dataset.popupId || popup.id || 'custom-popup';
    return `custom-popup:${popupId}`;
  };

  const getNow = () => Date.now();

  const getStoredData = (key) => {
    try {
      return JSON.parse(window.localStorage.getItem(key) || '{}');
    } catch (error) {
      return {};
    }
  };

  const setStoredData = (key, data) => {
    try {
      window.localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      return;
    }
  };

  const templateMatches = (popup) => {
    const rule = popup.dataset.displayRule || 'all';
    const templateName = popup.dataset.templateName || '';

    if (rule === 'all') return true;
    if (rule === 'home') return templateName === 'index';

    return templateName === rule;
  };

  const urlMatches = (popup) => {
    const currentUrl = window.location.pathname + window.location.search;
    const mustContain = popup.dataset.urlContains || '';
    const mustNotContain = popup.dataset.urlExcludes || '';

    if (mustContain && !currentUrl.includes(mustContain)) {
      return false;
    }

    if (mustNotContain && currentUrl.includes(mustNotContain)) {
      return false;
    }

    return true;
  };

  const displayRulesMatch = (popup) => {
    return templateMatches(popup) && urlMatches(popup);
  };

  const frequencyAllowsOpen = (popup) => {
    if (isThemeEditor() && popup.dataset.showInEditor === 'true') {
      return true;
    }

    const frequency = popup.dataset.frequency || 'days';
    const storageKey = getStorageKey(popup);
    const storedData = getStoredData(storageKey);

    if (frequency === 'always') return true;

    if (frequency === 'session') {
      return window.sessionStorage.getItem(storageKey) !== 'closed';
    }

    if (frequency === 'never_after_close') {
      return storedData.closed !== true;
    }

    if (frequency === 'days') {
      const expiresAt = Number(storedData.expiresAt || 0);
      return getNow() > expiresAt;
    }

    return true;
  };

  const shouldOpenPopup = (popup) => {
    if (popup.dataset.enabled !== 'true') return false;

    if (isThemeEditor() && popup.dataset.showInEditor === 'true') {
      return true;
    }

    return displayRulesMatch(popup) && frequencyAllowsOpen(popup);
  };

  const markPopupClosed = (popup) => {
    if (isThemeEditor()) return;

    const frequency = popup.dataset.frequency || 'days';
    const storageKey = getStorageKey(popup);

    if (frequency === 'always') return;

    if (frequency === 'session') {
      window.sessionStorage.setItem(storageKey, 'closed');
      return;
    }

    if (frequency === 'never_after_close') {
      setStoredData(storageKey, {
        closed: true
      });
      return;
    }

    if (frequency === 'days') {
      const days = Math.max(1, Number(popup.dataset.days || 7));
      const expiresAt = getNow() + days * 24 * 60 * 60 * 1000;

      setStoredData(storageKey, {
        expiresAt
      });
    }
  };

  const openPopup = (popup) => {
    if (popup.classList.contains('is-open')) return;

    popup.classList.add('is-open');
    document.documentElement.classList.add('custom-popup-open');
    document.body.classList.add('custom-popup-open');

    const dialog = popup.querySelector('.custom-popup__dialog');

    if (dialog) {
      dialog.focus();
    }
  };

  const closePopup = (popup) => {
    popup.classList.remove('is-open');
    document.documentElement.classList.remove('custom-popup-open');
    document.body.classList.remove('custom-popup-open');

    markPopupClosed(popup);
  };

  const bindCloseEvents = (popup) => {
    popup.querySelectorAll(CLOSE_SELECTOR).forEach((button) => {
      button.addEventListener('click', (event) => {
        if (
          button.classList.contains('custom-popup__overlay') &&
          popup.dataset.closeOnOverlay !== 'true'
        ) {
          return;
        }

        event.preventDefault();
        closePopup(popup);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!popup.classList.contains('is-open')) return;

      closePopup(popup);
    });
  };

  const bindTrigger = (popup) => {
    if (!shouldOpenPopup(popup)) return;

    const trigger = popup.dataset.trigger || 'delay';

    if (trigger === 'load') {
      openPopup(popup);
      return;
    }

    if (trigger === 'delay') {
      const delay = Math.max(0, Number(popup.dataset.delay || 0)) * 1000;

      window.setTimeout(() => {
        openPopup(popup);
      }, delay);

      return;
    }

    if (trigger === 'scroll') {
      const scrollPercent = Math.max(1, Number(popup.dataset.scrollPercent || 30));
      let hasOpened = false;

      const onScroll = () => {
        if (hasOpened) return;

        const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
        const currentScroll = window.scrollY || window.pageYOffset;
        const currentPercent = documentHeight > 0 ? (currentScroll / documentHeight) * 100 : 0;

        if (currentPercent >= scrollPercent) {
          hasOpened = true;
          window.removeEventListener('scroll', onScroll);
          openPopup(popup);
        }
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  };

  const initPopup = (popup) => {
    if (popup.dataset.customPopupInitialized === 'true') return;
    popup.dataset.customPopupInitialized = 'true';

    bindCloseEvents(popup);
    bindTrigger(popup);
  };

  const initAllPopups = () => {
    document.querySelectorAll(POPUP_SELECTOR).forEach(initPopup);
  };

  document.addEventListener('DOMContentLoaded', initAllPopups);

  document.addEventListener('shopify:section:load', (event) => {
    const popup = event.target.querySelector(POPUP_SELECTOR);

    if (popup) {
      initPopup(popup);
    }
  });

  document.addEventListener('shopify:section:select', (event) => {
    const popup = event.target.querySelector(POPUP_SELECTOR);

    if (popup) {
      openPopup(popup);
    }
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const popup = event.target.querySelector(POPUP_SELECTOR);

    if (popup) {
      closePopup(popup);
    }
  });
})();